import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import { getExportFilters } from '../../../graphql/custom-queries'
import { clearUserIdsOnStatusChange, getFiltersFromLS, IBalanceReportQuery, IExportFiltersResponse, reportResultsLimit, saveFiltersToLS } from '../../../components/reports/types'
import { useAppSelector } from '../../../store/hooks'
import { selectAuthCompanySlice } from '../../../store/auth-company-slice'
import IntlMessages from '@vacationtracker/shared/components/utils/IntlMessages'
import { PageHeader } from '../../../design-system/PageHeader'
import { FrontendUrls } from '../../../types/urls'
import { Display, IExportLeaveBalanceData, ReportPeriod, ReportType } from '@vacationtracker/shared/types/export'
import { roleAdmin, selectAuthUserSlice } from '../../../store/auth-user-slice'
import ExportModal from '../../../components/reports/export-modal'
import useLeaveReportData from '../../../components/reports/useGetExportData'
import LeaveBalanceReport from '../../../components/reports/leave-balance-report'
import { useManualQuery } from 'graphql-hooks'
import dayjs, { Dayjs } from 'dayjs'
import { useUsersForFilter } from '../../../hooks/use-users-for-filter'
import { useLocationFilterScope } from '../../../hooks/use-location-filter-scope'

const LeaveBalanceReportPage = () => {
  const { authCompany } = useAppSelector(selectAuthCompanySlice)
  const { authUser } = useAppSelector(selectAuthUserSlice)
  const reportType: ReportType = 'leave-balance-report'
  const { scopeLocations } = useLocationFilterScope()
  const amIAdmin = useSelector(roleAdmin)
  const isApprover = (authUser.approverToTeams?.length ?? 0) > 0
  // Location admins (also those who approve) see every department in their locations, so only plain approvers are narrowed.
  const scopeDepartmentsToApprover = isApprover && !amIAdmin

  const [companyData, setCompanyData] = useState<IExportFiltersResponse>()
  const [display, setDisplay] = useState<Display>(authCompany?.hourlyLeaveAccounting ? 'hours' : 'days')
  const [openDownloadModal, setOpenDownloadModal] = useState(false)
  const [getExportFiltersQuery] = useManualQuery<IExportFiltersResponse>(getExportFilters)
  const defaultDates: [Dayjs, Dayjs] = [dayjs().startOf('year'), dayjs().endOf('year')]

  const defaultQuery: IBalanceReportQuery = {
    page: 1,
    limit: reportResultsLimit,
    period: 'CURRENT_YEAR',
    startDate: defaultDates[0].format('YYYY-MM-DD'),
    endDate: defaultDates[1].format('YYYY-MM-DD'),
    userStatuses: ['ACTIVE'],
  }

  const [reportQuery, setReportQuery] = useState<IBalanceReportQuery>({ ...defaultQuery, ...getFiltersFromLS(reportType, authUser?.id as string) } as IBalanceReportQuery)
  const { exportData, total, loading, getResults, clearCache } = useLeaveReportData()

  const { usersForFilter, loadingUsersForFilter } = useUsersForFilter(reportQuery.userStatuses as string[] | undefined)

  useEffect(() => {
    getCompanyData()
  }, [])

  useEffect(() => {
    getResults(reportQuery, 'leave-balance-report')
  }, [reportQuery, getResults])

  const getCompanyData = async () => {
    const data = await getExportFiltersQuery()
    if (data.data && scopeDepartmentsToApprover) {
      const approverTeamIds = new Set((authUser.approverToTeams ?? []).map((t) => t.id))
      data.data = {
        ...data.data,
        getTeamList: data.data.getTeamList.filter((t) => approverTeamIds.has(t.id)),
      }
    }
    setCompanyData(data.data)
  }

  const handleFiltersChange = (filters) => {
    clearCache()
    const existingStartDate = reportQuery.startDate
    const existingEndDate = reportQuery.endDate
    const existingPeriod = reportQuery.period

    clearUserIdsOnStatusChange(filters, reportQuery.userStatuses as string[] | undefined)
    saveFiltersToLS(filters, reportType, authUser?.id as string)
    const query: IBalanceReportQuery = isEmpty(filters)
      ? (() => {
          //on clearing filters we need to remove userStatuses from default query
          const newQuery = { ...defaultQuery }
          delete newQuery.userStatuses
          return newQuery
        })()
      : { ...reportQuery, ...filters, page: 1 }
    query.lastId = undefined
    query.startDate = existingStartDate
    query.endDate = existingEndDate
    query.period = existingPeriod
    setReportQuery(query)
  }

  const handlePeriodChange = (dates: [Dayjs, Dayjs], period?: ReportPeriod) => {
    if (!period) {
      period = reportQuery.period || 'CURRENT_YEAR'
    }
    clearCache()
    const query = { ...reportQuery, page: 1, startDate: dates[0].format('YYYY-MM-DD'), endDate: dates[1].format('YYYY-MM-DD'), period }
    query.lastId = undefined
    setReportQuery(query)
  }

  const handleDisplayChange = (display: Display) => {
    setDisplay(display)
  }

  const loadNext = () => {
    const query = { ...reportQuery, lastId: last(exportData as IExportLeaveBalanceData[])?.userId, page: (reportQuery.page as number) + 1 }
    setReportQuery(query)
  }

  const loadPrevious = () => {
    const query = { ...reportQuery, page: Math.max((reportQuery.page as number) - 1, 1) }
    setReportQuery(query)
  }

  // Create companyData with dynamically fetched users for the filter
  // While loading, show empty array instead of falling back to stale data
  const companyDataWithDynamicUsers = companyData
    ? {
        ...companyData,
        getLocationList: scopeLocations(companyData.getLocationList),
        getUsersFiltered: {
          users: loadingUsersForFilter ? [] : usersForFilter.length > 0 ? usersForFilter : companyData.getUsersFiltered.users,
        },
      }
    : undefined

  return (
    <div className="main-content" id="report">
      <PageHeader title={<IntlMessages id="export.leaveBalanceReport" />} parent={[{ label: <IntlMessages id="sidebar.reports" />, to: FrontendUrls.reports }]} />
      {companyDataWithDynamicUsers && (
        <LeaveBalanceReport
          companyData={companyDataWithDynamicUsers}
          defaultDates={defaultDates}
          calculationStartYear={authCompany?.calculationStartYear as string | undefined}
          onFiltersChange={handleFiltersChange}
          onPeriodChange={handlePeriodChange}
          onDisplayChange={handleDisplayChange}
          defaultFilters={isEmpty(getFiltersFromLS(reportType, authUser?.id as string)) ? { userStatuses: ['ACTIVE'] } : getFiltersFromLS(reportType, authUser?.id as string)}
          display={display}
          loadNext={loadNext}
          loadPrevious={loadPrevious}
          download={() => setOpenDownloadModal(true)}
          loading={loading}
          data={exportData as IExportLeaveBalanceData[]}
          page={reportQuery.page || 1}
          total={total}
        />
      )}
      <ExportModal
        open={openDownloadModal}
        onClose={() => setOpenDownloadModal(false)}
        total={total}
        reportQuery={reportQuery}
        reportType={reportType}
        title="export.leaveBalanceReport"
        display={display}
        authUser={authUser}
      />
    </div>
  )
}

export default LeaveBalanceReportPage
